// ============================================
// FILE 5: /frontend/src/components/listings/SharesSelector.jsx
// PURPOSE: Shares purchase selector (replaces ChamaCalculator)
// CHANGES: New component for shares-based model
// ============================================

import { useState, useEffect } from 'react';
import { TrendingUp, Loader2, Info, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { chamaAPI } from '../../api/chama.api';

export default function SharesSelector({ listingId, onPurchaseClick }) {
  const [sharesInfo, setSharesInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharesWanted, setSharesWanted] = useState(1);

  useEffect(() => {
    if (listingId) {
      fetchSharesInfo();
    }
  }, [listingId]);

  const fetchSharesInfo = async () => {
    try {
      const response = await chamaAPI.getSharesInfo(listingId);
      setSharesInfo(response);
    } catch (error) {
      console.error('Failed to fetch shares info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSharesChange = (value) => {
    const shares = parseInt(value) || 0;
    if (shares >= 0 && shares <= sharesInfo.stats.sharesRemaining) {
      setSharesWanted(shares);
    }
  };

  const calculateTotal = () => {
    if (!sharesInfo) return { total: 0, deposit: 0, ownership: 0 };
    const sharePrice = parseFloat(sharesInfo.listing.sharePrice);
    const total = sharePrice * sharesWanted;
    const deposit = total * 0.1;
    const ownership = ((sharesWanted / sharesInfo.listing.totalShares) * 100).toFixed(2);
    return { total, deposit, ownership: parseFloat(ownership) };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6 sm:py-8">
          <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!sharesInfo || !sharesInfo.listing.allowGroupBuy) {
    return null;
  }

  const { total, deposit, ownership } = calculateTotal();
  const { stats, listing } = sharesInfo;

  if (stats.isFunded) {
    return (
      <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardContent className="p-3 sm:p-4 md:p-6 text-center py-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-2 sm:mb-3">
            <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-green-900 mb-1 sm:mb-2">
            100% Funded!
          </h3>
          <p className="text-xs sm:text-sm text-green-700">
            This property has been fully funded through group buying.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (stats.isExpired) {
    return (
      <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-pink-50">
        <CardContent className="p-3 sm:p-4 md:p-6 text-center py-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-2 sm:mb-3">
            <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-red-900 mb-1 sm:mb-2">
            Funding Period Expired
          </h3>
          <p className="text-xs sm:text-sm text-red-700">
            The funding deadline has passed for this listing.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full border-2 border-purple-200">
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          Buy Shares
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                <p>Purchase shares to co-own this property. Pay 10% deposit to reserve your shares.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground mt-1">
          Own a percentage of this property by buying shares
        </p>
      </CardHeader>

      <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6">
        {/* Shares Info */}
        <div className="bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
            <div>
              <p className="text-purple-700 text-[10px] sm:text-xs">Total Shares</p>
              <p className="font-semibold text-purple-900">{listing.totalShares}</p>
            </div>
            <div>
              <p className="text-purple-700 text-[10px] sm:text-xs">Price Per Share</p>
              <p className="font-semibold text-purple-900">
                KES {parseFloat(listing.sharePrice).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-purple-700 text-[10px] sm:text-xs">Available</p>
              <p className="font-semibold text-green-600">{stats.sharesRemaining}</p>
            </div>
            <div>
              <p className="text-purple-700 text-[10px] sm:text-xs">Sold</p>
              <p className="font-semibold text-blue-600">{listing.sharesSold}</p>
            </div>
          </div>
        </div>

        {/* Funding Deadline */}
        {listing.fundingDeadline && (
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>
              Funding closes: {new Date(listing.fundingDeadline).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Shares Input */}
        <div className="space-y-1.5 sm:space-y-2">
          <Label htmlFor="sharesWanted" className="text-xs sm:text-sm">
            Number of Shares
          </Label>
          <Input
            id="sharesWanted"
            type="number"
            min="1"
            max={stats.sharesRemaining}
            value={sharesWanted}
            onChange={(e) => handleSharesChange(e.target.value)}
            className="h-8 sm:h-9 md:h-10 text-xs sm:text-sm"
          />
          <p className="text-[10px] sm:text-xs text-muted-foreground">
            Max: {stats.sharesRemaining} shares available
          </p>
        </div>

        {/* Quick Select Buttons */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {[1, 5, 10, 20].filter(num => num <= stats.sharesRemaining).map((num) => (
            <Button
              key={num}
              variant="outline"
              size="sm"
              onClick={() => setSharesWanted(num)}
              className="h-7 sm:h-8 text-[10px] sm:text-xs px-2 sm:px-3"
            >
              {num} {num === 1 ? 'share' : 'shares'}
            </Button>
          ))}
        </div>

        {/* Calculation Results */}
        {sharesWanted > 0 && (
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3 border border-purple-200">
            <h4 className="font-semibold text-xs sm:text-sm md:text-base text-purple-900">
              Your Investment:
            </h4>
            
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[10px] sm:text-xs md:text-sm text-purple-700">
                  Ownership:
                </span>
                <Badge variant="secondary" className="text-xs sm:text-sm font-bold">
                  {ownership}%
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[10px] sm:text-xs md:text-sm text-purple-700">
                  Total Amount:
                </span>
                <span className="font-bold text-xs sm:text-sm md:text-base text-purple-900">
                  KES {total.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center pt-1.5 sm:pt-2 border-t border-purple-300">
                <span className="text-[10px] sm:text-xs md:text-sm font-semibold text-purple-900">
                  10% Deposit Required:
                </span>
                <span className="font-bold text-sm sm:text-base md:text-lg text-purple-900">
                  KES {deposit.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="bg-white/50 rounded p-2 text-[9px] sm:text-[10px] text-purple-800">
              💡 Pay 10% deposit to reserve your shares. Full payment due within 90 days of 100% funding.
            </div>

            <Button
              onClick={() => onPurchaseClick({ sharesWanted, total, deposit, ownership })}
              disabled={sharesWanted < 1 || sharesWanted > stats.sharesRemaining}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-9 sm:h-10 md:h-11 text-xs sm:text-sm"
            >
              Reserve {sharesWanted} {sharesWanted === 1 ? 'Share' : 'Shares'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}